/**
 * BidToolPage — owner/admin bidding tool.
 *
 * A generic cleaning-job bid calculator: pick a job-type preset, tune the
 * inputs, and the deterministic engine (src/lib/bidding/engine.ts) returns
 * labor-hours, cost, suggested bid, and margin. Bids can be saved, listed,
 * and reloaded to edit.
 *
 * Access: route is RequireRole-gated to super_admin + contractor_admin.
 * This page additionally gates on the `bidding_tool` tenant feature flag
 * so the tool can be rolled out per tenant (demo org first). The flag is
 * the rollout lever; the role gate is the security boundary.
 *
 * No AI here — the AI intake layer is a separate, later phase. All pricing
 * is the engine's; this page only collects inputs and renders results.
 */

import { useMemo, useState, type ReactNode } from 'react'
import { useTranslation }                     from 'react-i18next'
import { useTenantFeatures }                  from '@/hooks/useTenantFeatures'
import { useBids, BID_STATUSES, type BidRow, type BidStatus } from '@/hooks/useBids'
import { useSaveBid }                         from '@/hooks/useSaveBid'
import { useBidIntake }                       from '@/hooks/useBidIntake'
import {
  computeBid,
  formatUsdFromCents,
  type BidInputs,
}                                             from '@/lib/bidding/engine'
import {
  BID_PRESETS,
  DEFAULT_PRESET_KEY,
  presetByKey,
}                                             from '@/lib/bidding/presets'

function roundHours(hours: number): string {
  return (Math.round(hours * 10) / 10).toLocaleString('en-US')
}

export function BidToolPage(): ReactNode {
  const { t } = useTranslation()
  const features = useTenantFeatures()
  const bids     = useBids()
  const saveBid  = useSaveBid()
  const intake   = useBidIntake()

  const [presetKey, setPresetKey]   = useState<string>(DEFAULT_PRESET_KEY)
  const [inputs, setInputs]         = useState<BidInputs>(() => ({ ...presetByKey(DEFAULT_PRESET_KEY).defaults }))
  const [title, setTitle]           = useState('')
  const [clientName, setClientName] = useState('')
  const [status, setStatus]         = useState<BidStatus>('draft')
  const [notes, setNotes]           = useState('')
  const [editingId, setEditingId]   = useState<string | null>(null)
  const [savedMsg, setSavedMsg]     = useState(false)
  const [aiDescription, setAiDescription] = useState('')
  const [aiNotes, setAiNotes]       = useState('')

  const preset      = presetByKey(presetKey)
  const computation = useMemo(() => computeBid(inputs), [inputs])

  function applyPreset(key: string): void {
    setPresetKey(key)
    setInputs({ ...presetByKey(key).defaults })
    setEditingId(null)
    setSavedMsg(false)
  }

  function setField<K extends keyof BidInputs>(key: K, value: number): void {
    setInputs((prev) => ({ ...prev, [key]: value }))
    setSavedMsg(false)
  }

  function loadBid(row: BidRow): void {
    setEditingId(row.id)
    setPresetKey(BID_PRESETS.some((p) => p.key === row.job_type) ? row.job_type : 'custom')
    setInputs({ ...presetByKey('custom').defaults, ...row.inputs_json })
    setTitle(row.title)
    setClientName(row.client_name ?? '')
    setStatus(row.status)
    setNotes(row.notes ?? '')
    setSavedMsg(false)
  }

  function resetForm(): void {
    setEditingId(null)
    setTitle('')
    setClientName('')
    setStatus('draft')
    setNotes('')
    applyPreset(DEFAULT_PRESET_KEY)
  }

  function onSave(): void {
    saveBid.mutate(
      {
        id:          editingId ?? undefined,
        title,
        clientName:  clientName.trim() ? clientName : null,
        jobType:     presetKey,
        status,
        notes:       notes.trim() ? notes : null,
        inputs,
        computation,
      },
      {
        onSuccess: (row) => {
          setEditingId(row.id)
          setSavedMsg(true)
        },
      },
    )
  }

  function onDraftWithAi(): void {
    setAiNotes('')
    intake.mutate(aiDescription.trim(), {
      onSuccess: (res) => {
        if (res.status === 'ok' && res.inputs) {
          setInputs(res.inputs)
          setPresetKey('custom')
          setEditingId(null)
          setAiNotes(res.notes)
          setSavedMsg(false)
        }
      },
    })
  }

  const aiResult  = intake.data
  const canDraft  = aiDescription.trim().length > 0 && !intake.isPending
  const canSave   = title.trim().length > 0 && !saveBid.isPending

  if (!features.isLoading && !features.has('bidding_tool')) {
    return (
      <main data-testid="bid-tool-page" style={pageStyle}>
        <h1 style={titleStyle}>{t('bidTool.title', 'Bid tool')}</h1>
        <p data-testid="bid-tool-disabled" style={{ color: 'var(--brand-neutral-700)' }}>
          {t('bidTool.disabled', 'The bidding tool is not enabled for this organization yet.')}
        </p>
      </main>
    )
  }

  return (
    <main data-testid="bid-tool-page" style={pageStyle}>
      <h1 style={titleStyle}>{t('bidTool.title', 'Bid tool')}</h1>
      <p style={{ margin: 0, color: 'var(--brand-neutral-700)', fontSize: '0.9rem' }}>
        {t('bidTool.intro', 'Build a defensible bid for any cleaning job. Pick a job-type preset, tune the inputs, save the bid.')}
      </p>

      {/* ─── AI draft ─────────────────────────────────────────── */}
      <Section testId="bid-tool-ai" heading={t('bidTool.aiDraft', 'Draft with AI')}>
        <p style={hintStyle}>
          {t('bidTool.aiHint', 'Describe the job in plain English — the AI fills the inputs below. It never sets the price; the calculator does. Review before saving.')}
        </p>
        <textarea
          data-testid="bid-ai-description"
          value={aiDescription}
          onChange={(e) => setAiDescription(e.target.value)}
          rows={3}
          placeholder={t('bidTool.aiPlaceholder', 'e.g. 24 two-bed student apartments, trashed at move-out, half are 2-bath; clean ovens, fridges, fans, vents.')}
          style={controlStyle}
        />
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" data-testid="bid-ai-draft" onClick={onDraftWithAi} disabled={!canDraft} style={primaryBtn(canDraft)}>
            {intake.isPending ? t('bidTool.aiDrafting', 'Drafting…') : t('bidTool.aiButton', 'Draft with AI')}
          </button>
          {aiResult?.status === 'ok' && aiNotes && (
            <span data-testid="bid-ai-notes" style={{ fontSize: '0.8rem', color: 'var(--brand-neutral-700)' }}>{aiNotes}</span>
          )}
          {aiResult?.status === 'unavailable' && (
            <span role="alert" data-testid="bid-ai-unavailable" style={{ fontSize: '0.85rem', color: 'var(--brand-danger)' }}>
              {t('bidTool.aiUnavailable', 'AI is temporarily unavailable — fill the inputs manually or try again shortly.')}
            </span>
          )}
          {aiResult?.status === 'malformed' && (
            <span role="alert" data-testid="bid-ai-malformed" style={{ fontSize: '0.85rem', color: 'var(--brand-warning, #b45309)' }}>
              {t('bidTool.aiMalformed', 'Could not read that into inputs — try rephrasing the description.')}
            </span>
          )}
          {intake.error && (
            <span role="alert" data-testid="bid-ai-error" style={{ fontSize: '0.85rem', color: 'var(--brand-danger)' }}>
              {t('bidTool.aiError', 'AI draft request failed.')}
            </span>
          )}
        </div>
      </Section>

      <div style={twoColStyle}>
        {/* ─── Inputs ─────────────────────────────────────────── */}
        <Section testId="bid-tool-inputs" heading={t('bidTool.inputs', 'Job inputs')}>
          <Field label={t('bidTool.preset', 'Job type / preset')}>
            <select
              data-testid="bid-preset"
              value={presetKey}
              onChange={(e) => applyPreset(e.target.value)}
              style={controlStyle}
            >
              {BID_PRESETS.map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
          </Field>
          <p data-testid="bid-preset-note" style={hintStyle}>{preset.description}</p>

          <NumberField testId="bid-unit-count" label={t('bidTool.unitCount', 'Number of units')}
            value={inputs.unitCount} step={1} min={0} onChange={(v) => setField('unitCount', v)} />
          <NumberField testId="bid-base-min" label={t('bidTool.baseMin', 'Base clean time per unit (min)')}
            value={inputs.baseLaborMinutesPerUnit} step={5} min={0} onChange={(v) => setField('baseLaborMinutesPerUnit', v)} />
          <NumberField testId="bid-addon-min" label={t('bidTool.addOnMin', 'Add-ons per unit (min) — e.g. 2nd bath ≈ 60')}
            value={inputs.addOnMinutesPerUnit} step={5} min={0} onChange={(v) => setField('addOnMinutesPerUnit', v)} />
          <NumberField testId="bid-soil" label={t('bidTool.soil', 'Soil / condition multiplier')}
            value={inputs.soilMultiplier} step={0.1} min={0} onChange={(v) => setField('soilMultiplier', v)} />
          <NumberField testId="bid-crew" label={t('bidTool.crew', 'Crew size (people per unit)')}
            value={inputs.crewSize} step={1} min={1} onChange={(v) => setField('crewSize', v)} />
          <DollarField testId="bid-rate" label={t('bidTool.rate', 'Fully-burdened labor ($/hr)')}
            cents={inputs.burdenedHourlyRateCents} onChange={(c) => setField('burdenedHourlyRateCents', c)} />
          <NumberField testId="bid-markup" label={t('bidTool.markup', 'Markup multiplier (× burdened labor)')}
            value={inputs.markupMultiplier} step={0.05} min={0} onChange={(v) => setField('markupMultiplier', v)} />
          <DollarField testId="bid-materials" label={t('bidTool.materials', 'Materials / equipment (one-off $)')}
            cents={inputs.flatMaterialsCents} onChange={(c) => setField('flatMaterialsCents', c)} />
        </Section>

        {/* ─── Results ────────────────────────────────────────── */}
        <Section testId="bid-tool-results" heading={t('bidTool.results', 'Estimate')}>
          <Result testId="bid-result-perunit-hours" label={t('bidTool.perUnitHours', 'Labor per unit')}
            value={`${roundHours(computation.perUnitLaborMinutes / 60)} hr`} />
          <Result testId="bid-result-total-hours" label={t('bidTool.totalHours', 'Total labor')}
            value={`${roundHours(computation.totalLaborHours)} hr`} />
          <Result testId="bid-result-wallclock" label={t('bidTool.wallClock', 'Wall-clock (with crew)')}
            value={`${roundHours(computation.wallClockHours)} hr`} />
          <Result testId="bid-result-labor-cost" label={t('bidTool.laborCost', 'Burdened labor cost')}
            value={formatUsdFromCents(computation.laborCostCents)} />
          <Result testId="bid-result-materials" label={t('bidTool.materialsCost', 'Materials')}
            value={formatUsdFromCents(computation.materialsCents)} />
          <Result testId="bid-result-total-cost" label={t('bidTool.totalCost', 'Total cost')}
            value={formatUsdFromCents(computation.totalCostCents)} />
          <Result testId="bid-result-perunit-bid" label={t('bidTool.perUnitBid', 'Suggested bid per unit')}
            value={formatUsdFromCents(computation.perUnitBidCents)} />
          <Result testId="bid-result-margin" label={t('bidTool.margin', 'Gross margin')}
            value={`${Math.round(computation.grossMarginPct * 1000) / 10}%`} />
          <div
            data-testid="bid-result-total-bid"
            data-cents={computation.suggestedBidCents}
            style={totalBidStyle}
          >
            <span style={{ fontSize: '0.8rem', color: 'var(--brand-neutral-700)' }}>
              {t('bidTool.totalBid', 'Suggested bid (total)')}
            </span>
            <strong style={{ fontSize: '1.8rem' }}>{formatUsdFromCents(computation.suggestedBidCents)}</strong>
          </div>
          {presetKey === 'dorm_turnover_houston' && (
            <p data-testid="bid-sanity-note" style={hintStyle}>
              {t('bidTool.dormSanity', 'Houston market check: hold a ~$0.30/sq ft floor; walk below ~$275/unit. See docs/bidding.')}
            </p>
          )}
        </Section>
      </div>

      {/* ─── Save ─────────────────────────────────────────────── */}
      <Section testId="bid-tool-save" heading={editingId ? t('bidTool.editBid', 'Edit bid') : t('bidTool.saveBid', 'Save bid')}>
        <Field label={t('bidTool.bidTitle', 'Bid title')}>
          <input data-testid="bid-title" value={title} onChange={(e) => { setTitle(e.target.value); setSavedMsg(false) }}
            placeholder={t('bidTool.titlePlaceholder', 'e.g. HC Apartments — summer turn')} style={controlStyle} />
        </Field>
        <Field label={t('bidTool.client', 'Client (optional)')}>
          <input data-testid="bid-client" value={clientName} onChange={(e) => setClientName(e.target.value)}
            style={controlStyle} />
        </Field>
        <Field label={t('bidTool.status', 'Status')}>
          <select data-testid="bid-status" value={status} onChange={(e) => setStatus(e.target.value as BidStatus)} style={controlStyle}>
            {BID_STATUSES.map((s) => (
              <option key={s} value={s}>{t(`bidTool.status_${s}`, s)}</option>
            ))}
          </select>
        </Field>
        <Field label={t('bidTool.notes', 'Notes (optional)')}>
          <textarea data-testid="bid-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} style={controlStyle} />
        </Field>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" data-testid="bid-save" onClick={onSave} disabled={!canSave} style={primaryBtn(canSave)}>
            {saveBid.isPending
              ? t('bidTool.saving', 'Saving…')
              : editingId ? t('bidTool.update', 'Update bid') : t('bidTool.create', 'Save bid')}
          </button>
          {editingId && (
            <button type="button" data-testid="bid-new" onClick={resetForm} style={secondaryBtn}>
              {t('bidTool.newBid', 'New bid')}
            </button>
          )}
          {savedMsg && <span data-testid="bid-saved" style={{ color: 'var(--brand-success, green)', fontSize: '0.85rem' }}>{t('bidTool.saved', 'Saved.')}</span>}
          {saveBid.error && (
            <span role="alert" data-testid="bid-save-error" style={{ color: 'var(--brand-danger)', fontSize: '0.85rem' }}>
              {t('bidTool.saveError', 'Could not save the bid.')}
            </span>
          )}
        </div>
      </Section>

      {/* ─── Saved bids ───────────────────────────────────────── */}
      <Section testId="bid-tool-saved-list" heading={t('bidTool.savedBids', 'Saved bids')}>
        {bids.isLoading ? (
          <p style={hintStyle}>{t('bidTool.loading', 'Loading…')}</p>
        ) : bids.error ? (
          <p role="alert" data-testid="bid-list-error" style={{ color: 'var(--brand-danger)', fontSize: '0.85rem' }}>
            {t('bidTool.listError', 'Could not load saved bids.')}
          </p>
        ) : (bids.data?.length ?? 0) === 0 ? (
          <p data-testid="bid-list-empty" style={hintStyle}>{t('bidTool.noBids', 'No saved bids yet.')}</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="bid-list-table" style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--brand-neutral-700)' }}>
                  <th style={cellStyle}>{t('bidTool.colTitle', 'Title')}</th>
                  <th style={cellStyle}>{t('bidTool.colClient', 'Client')}</th>
                  <th style={cellStyle}>{t('bidTool.colUnits', 'Units')}</th>
                  <th style={cellStyle}>{t('bidTool.colBid', 'Bid')}</th>
                  <th style={cellStyle}>{t('bidTool.colStatus', 'Status')}</th>
                  <th style={cellStyle} />
                </tr>
              </thead>
              <tbody>
                {bids.data?.map((row) => (
                  <tr key={row.id} data-testid={`bid-row-${row.id}`}>
                    <td style={cellStyle}>{row.title}</td>
                    <td style={cellStyle}>{row.client_name ?? '—'}</td>
                    <td style={cellStyle}>{row.unit_count}</td>
                    <td style={cellStyle}>{formatUsdFromCents(row.total_bid_cents)}</td>
                    <td style={cellStyle}>{t(`bidTool.status_${row.status}`, row.status)}</td>
                    <td style={cellStyle}>
                      <button type="button" data-testid={`bid-load-${row.id}`} onClick={() => loadBid(row)} style={secondaryBtn}>
                        {t('bidTool.load', 'Load')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </main>
  )
}

// ─── Local presentational helpers ─────────────────────────────────────

function Section({ testId, heading, children }: { testId: string; heading: string; children: ReactNode }): ReactNode {
  return (
    <section data-testid={testId} style={sectionStyle}>
      <h2 style={{ margin: 0, fontSize: '1rem' }}>{heading}</h2>
      {children}
    </section>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.8rem', color: 'var(--brand-neutral-700)' }}>
      {label}
      {children}
    </label>
  )
}

function NumberField(
  { testId, label, value, onChange, step, min }:
  { testId: string; label: string; value: number; onChange: (v: number) => void; step: number; min: number },
): ReactNode {
  return (
    <Field label={label}>
      <input
        type="number"
        data-testid={testId}
        value={Number.isFinite(value) ? value : 0}
        step={step}
        min={min}
        onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
        style={controlStyle}
      />
    </Field>
  )
}

function DollarField(
  { testId, label, cents, onChange }:
  { testId: string; label: string; cents: number; onChange: (cents: number) => void },
): ReactNode {
  const dollars = Number.isFinite(cents) ? cents / 100 : 0
  return (
    <Field label={label}>
      <input
        type="number"
        data-testid={testId}
        value={dollars}
        step={0.5}
        min={0}
        onChange={(e) => onChange(e.target.value === '' ? 0 : Math.round(Number(e.target.value) * 100))}
        style={controlStyle}
      />
    </Field>
  )
}

function Result({ testId, label, value }: { testId: string; label: string; value: string }): ReactNode {
  return (
    <div data-testid={testId} style={resultRowStyle}>
      <span style={{ color: 'var(--brand-neutral-700)' }}>{label}</span>
      <strong>{value}</strong>
    </div>
  )
}

// ─── Styles ───────────────────────────────────────────────────────────

const pageStyle = { display: 'flex', flexDirection: 'column' as const, gap: '1rem', padding: '1rem' }
const titleStyle = { margin: 0, fontSize: '1.4rem', fontFamily: 'var(--brand-font-family-display)' }
const twoColStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }
const sectionStyle = {
  background: 'var(--brand-white)', border: '1px solid var(--brand-neutral-300)',
  borderRadius: 'var(--brand-radius-lg)', padding: '1rem',
  display: 'flex', flexDirection: 'column' as const, gap: '0.6rem',
}
const controlStyle = {
  padding: '0.4rem 0.5rem', border: '1px solid var(--brand-neutral-300)',
  borderRadius: 'var(--brand-radius-sm)', fontSize: '0.9rem', width: '100%',
}
const hintStyle = { margin: 0, fontSize: '0.75rem', color: 'var(--brand-neutral-500)' }
const resultRowStyle = {
  display: 'flex', justifyContent: 'space-between', gap: '1rem',
  fontSize: '0.9rem', padding: '0.2rem 0',
  borderBottom: '1px solid var(--brand-neutral-100)',
}
const totalBidStyle = {
  marginTop: '0.4rem', padding: '0.6rem', background: 'var(--brand-primary-subtle)',
  borderRadius: 'var(--brand-radius-sm)', display: 'flex', flexDirection: 'column' as const, gap: '0.2rem',
}
const cellStyle = { padding: '0.35rem 0.6rem', borderBottom: '1px solid var(--brand-neutral-100)' }

function primaryBtn(enabled: boolean): Record<string, string> {
  return {
    padding: '0.5rem 0.9rem', background: enabled ? 'var(--brand-primary)' : 'var(--brand-neutral-300)',
    color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--brand-radius-md)',
    fontSize: '0.9rem', cursor: enabled ? 'pointer' : 'not-allowed', fontWeight: '600',
  }
}
const secondaryBtn = {
  padding: '0.35rem 0.7rem', background: 'transparent', color: 'var(--brand-neutral-700)',
  border: '1px solid var(--brand-neutral-300)', borderRadius: 'var(--brand-radius-sm)',
  fontSize: '0.8rem', cursor: 'pointer',
}
